import React from 'react';

/**
 * The X (formerly Twitter) brand mark — rendered as a CONTAINED badge:
 * a white X glyph on a dark rounded square (X's own app-icon form).
 *
 * Why contained and not a bare X glyph: Feather's `name="x"` close icon is used
 * throughout the app (every modal header / dismiss). A bare X letterform for the
 * brand would make the same shape mean both "close this" and "the social network"
 * — the icon-overloading that §3.4 forbids (the rule that caught the Heart being
 * reused for reports). The rounded-square containment is what disambiguates the
 * brand mark from a close button, and it matches how X renders its own logo.
 *
 * react-feather has no X mark (and won't — it's effectively unmaintained), and
 * §3.1 forbids introducing a second icon library, so this is a small inline SVG
 * that mirrors the feather API (`size` + `color`) as a drop-in for `<Twitter/>`.
 * `color` sets the SQUARE fill (default black); the glyph is always white. Pass a
 * concrete hex or leave the default — never a CSS `var()` (see the feather var()
 * note in project memory; SVG fill doesn't resolve vars).
 */
interface XIconProps {
  size?: number | string;
  /** Rounded-square background color. Glyph stays white. Default black. */
  color?: string;
  className?: string;
  style?: React.CSSProperties;
  'aria-hidden'?: boolean | 'true' | 'false';
  'aria-label'?: string;
}

export const XIcon: React.FC<XIconProps> = ({
  size = 24,
  color = '#000000',
  className,
  style,
  ...rest
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    style={style}
    {...rest}
  >
    <rect width="24" height="24" rx="5" fill={color} />
    <path
      transform="translate(12 12) scale(0.62) translate(-12 -12)"
      fill="#FFFFFF"
      d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
    />
  </svg>
);

export default XIcon;
